import { useState, useRef, lazy, Suspense } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Loader2, Send, Smile, Image as ImageIcon, X } from "lucide-react";
import type { EmojiClickData, Theme } from "emoji-picker-react";
import { useAuth } from "../hooks/use-auth";
import { uploadCommentMedia, convertImageToJPEG, createComment } from "../lib/data";
import { imageErrorMessage } from "../lib/image-prepare";
import { db } from "../lib/firebase";
import { collection, doc } from "@firebase/firestore";
import { isVideoFile, startVideoUpload, validateVideoFile } from "../lib/video-upload";
import { pickNativeMedia } from "../lib/native-media-picker";
import { MediaAttachMenu } from "./media-attach-menu";
import { VideoFileTile } from "./video-file-tile";

// Desktop-only and large (~400 KB), so it's loaded on first use.
const EmojiPicker = lazy(() => import("emoji-picker-react"));

interface CommentInputProps {
    postId: string;
    onComment: (content: string, mediaUrl?: string, mediaType?: "image" | "video" | "gif" | "sticker") => Promise<void>;
    isMobile?: boolean;
}

export function CommentInput({ postId, onComment, isMobile = false }: CommentInputProps) {
    const { user } = useAuth();
    const [content, setContent] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
    const [fileError, setFileError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if ((!content.trim() && !selectedFile) || !user) return;

        // Videos upload in the background; the comment is created when the upload finishes.
        if (selectedFile && isVideoFile(selectedFile)) {
            const commentId = doc(collection(db, "posts", postId, "comments")).id;
            const commentContent = content;
            startVideoUpload({
                label: "Komentar",
                file: selectedFile,
                folder: "comments",
                metadata: { refPath: `posts/${postId}/comments/${commentId}` },
                onUploaded: async ({ url, storagePath }) => {
                    await createComment(postId, user.id, commentContent, url, "video", {
                        id: commentId,
                        extra: { videoProcessing: true, videoProcessingPath: storagePath },
                    });
                    return `posts/${postId}/comments/${commentId}`;
                },
            });
            setContent("");
            clearFile();
            return;
        }

        setIsSubmitting(true);
        setFileError(null);
        try {
            let mediaUrl = undefined;
            let mediaType: "image" | "video" | "gif" | "sticker" | undefined = undefined;

            if (selectedFile) {
                mediaUrl = await uploadCommentMedia(selectedFile);
                if (selectedFile.type.startsWith("image/gif")) mediaType = "gif";
                else if (selectedFile.type.startsWith("image/")) mediaType = "image";
                else if (selectedFile.type.startsWith("video/")) mediaType = "video";
            }

            await onComment(content, mediaUrl, mediaType);
            setContent("");
            setSelectedFile(null);
            setPreviewUrl(null);
        } catch (error) {
            console.error("Failed to post comment:", error);
            setFileError(
                (error as Error)?.name === "UploadStalledError"
                    ? (error as Error).message
                    : "Komentar nije objavljen. Provjerite vezu i pokušajte ponovno.",
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleEmojiClick = (emojiData: EmojiClickData) => {
        setContent((prev) => prev + emojiData.emoji);
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) void handlePickedFile(e.target.files[0]);
    };

    // Gallery in the iOS app (see MediaAttachMenu).
    const pickFromGallery = async () => {
        setFileError(null);
        try {
            const [file] = await pickNativeMedia({ limit: 1 }, () => setIsSubmitting(true));
            if (file) await handlePickedFile(file);
        } catch (err) {
            console.error("Gallery pick failed", err);
            setFileError("Odabrani medij se nije mogao učitati.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handlePickedFile = async (file: File) => {
        setIsSubmitting(true);
        setFileError(null);
        try {
            if (isVideoFile(file)) {
                const invalid = await validateVideoFile(file);
                if (invalid) {
                    setFileError(invalid);
                    clearFile();
                    return;
                }
            }
            let fileToProcess = file;
            if (file.type.startsWith("image/") || /\.hei[cf]$/i.test(file.name)) {
                fileToProcess = await convertImageToJPEG(file);
            }
            setSelectedFile(fileToProcess);
            const url = URL.createObjectURL(fileToProcess);
            setPreviewUrl(url);
        } catch (err) {
            console.error("Image conversion failed", err);
            setFileError(imageErrorMessage(err));
            clearFile();
        } finally {
            setIsSubmitting(false);
        }
    };

    const clearFile = () => {
        setSelectedFile(null);
        setPreviewUrl(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    if (!user) return null;

    return (
        <div className="flex gap-3 mt-4">
            <Avatar className="h-8 w-8">
                <AvatarImage src={user.avatar} alt={user.name} />
                <AvatarFallback>{user.name.charAt(0)}</AvatarFallback>
            </Avatar>
            <form onSubmit={handleSubmit} className="flex-1 space-y-2">
                <input
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    placeholder="Napiši komentar..."
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    disabled={isSubmitting}
                />

                {previewUrl && (
                    <div className="relative inline-block">
                        {selectedFile && isVideoFile(selectedFile) ? (
                            <VideoFileTile file={selectedFile} className="max-w-[240px]" />
                        ) : (
                            <img src={previewUrl} alt="Preview" className="h-20 w-auto rounded-md object-cover" />
                        )}
                        <button
                            type="button"
                            className="absolute -top-1 -right-1 h-5 w-5 rounded-full p-0 bg-red-500 text-white flex items-center justify-center"
                            onClick={clearFile}
                        >
                            <X className="h-3 w-3" />
                        </button>
                    </div>
                )}

                {fileError && (
                    <p className="text-xs text-red-500">{fileError}</p>
                )}

                {isEmojiPickerOpen && !isMobile && (
                    <div className="relative">
                        <div className="absolute bottom-0 left-0 z-50">
                            <Suspense fallback={null}>
                                <EmojiPicker onEmojiClick={handleEmojiClick} theme={"auto" as Theme} />
                            </Suspense>
                        </div>
                    </div>
                )}

                <div className="flex justify-between items-center">
                    <div className="flex gap-2">
                        {!isMobile && (
                            <button
                                type="button"
                                className="flex items-center gap-1 px-3 py-1.5 text-sm rounded-md text-muted-foreground hover:text-primary hover:bg-accent transition-colors"
                                onClick={() => setIsEmojiPickerOpen(!isEmojiPickerOpen)}
                            >
                                <Smile className="h-4 w-4" />
                                <span>Emoji</span>
                            </button>
                        )}
                        <MediaAttachMenu
                            onGallery={pickFromGallery}
                            onOtherFiles={() => fileInputRef.current?.click()}
                        >
                            <button
                                type="button"
                                className="flex items-center gap-1 px-3 py-1.5 text-sm rounded-md text-muted-foreground hover:text-primary hover:bg-accent transition-colors"
                            >
                                <ImageIcon className="h-4 w-4" />
                                <span>Slika</span>
                            </button>
                        </MediaAttachMenu>
                        <input
                            type="file"
                            ref={fileInputRef}
                            className="hidden"
                            accept="image/*,video/*"
                            onChange={handleFileSelect}
                        />
                    </div>

                    {(content || selectedFile) && (
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="flex items-center gap-2 px-4 py-1.5 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            {isSubmitting ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <Send className="h-4 w-4" />
                            )}
                            <span>Pošalji</span>
                        </button>
                    )}
                </div>
            </form>
        </div>
    );
}
